import type React from "react";
import { useEffect } from "react";
import { useAuth } from "./useAuth";
import { API_BASE_URL } from "@/lib/api-client";

interface Props {
  children: React.ReactNode;
}

export function AuthGuard({ children }: Props): React.JSX.Element {
  const { user, isLoading, isUnauthenticated, refetch } = useAuth();

  useEffect(() => {
    if (isUnauthenticated) {
      // Full-page navigation — this is the backend's OAuth entry point, proxied
      // through Vite in local dev; deployed builds go straight to the backend's
      // own origin via API_BASE_URL.
      window.location.href = `${API_BASE_URL}/auth/login`;
    }
  }, [isUnauthenticated]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading…</p>
      </div>
    );
  }

  // A real backend/network failure — not the same as "not logged in".
  // Redirecting here would loop the user through /auth/login forever.
  if (!user && !isUnauthenticated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-sm font-medium">Couldn't reach the server.</p>
          <p className="mt-1 text-sm text-muted-foreground">Please try again shortly.</p>
          <button
            type="button"
            onClick={refetch}
            className="mt-4 text-sm font-medium text-phoenix-orange hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center gap-3">
        <svg viewBox="0 0 93.5 110.5" className="w-6 h-6 text-phoenix-orange" aria-hidden="true" fill="currentColor">
          <circle cx="29.22" cy="46.75" r="5.84"/>
          <circle cx="46.75" cy="46.75" r="5.84"/>
          <circle cx="64.28" cy="46.75" r="5.84"/>
          <path d="M79.81,13.69C71.34,5.23,59.66,0,46.75,0S22.15,5.23,13.69,13.69C5.24,22.15,0,33.84,0,46.75s5.24,24.6,13.69,33.06l29.03,29.03c2.23,2.23,5.84,2.23,8.06,0l29.03-29.03c8.45-8.46,13.69-20.15,13.69-33.06s-5.24-24.6-13.69-33.06ZM46.75,85.44c-21.37,0-38.7-17.32-38.7-38.7S25.38,8.05,46.75,8.05s38.7,17.32,38.7,38.7-17.32,38.7-38.7,38.7Z"/>
        </svg>
        <p className="text-muted-foreground text-sm">Redirecting…</p>
      </div>
    );
  }

  return <>{children}</>;
}
